import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  InputAdornment,
  LinearProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Alert,
  Paper,
  Tooltip,
  Badge,
  Rating,
} from '@mui/material';
import {
  Search,
  Add,
  Download,
  Visibility,
  Message,
  MoreVert,
  Person,
  DirectionsCar,
  Business,
  Groups,
  AccountBalance,
  Warning,
  Security,
  Flag,
  Block,
  Schedule,
  CheckCircle,
  Cancel,
  SwapHoriz,
  Assessment,
  TrendingDown,
  TrendingUp,
  FilterList,
  Send,
  Edit,
} from '@mui/icons-material';

interface User {
  id: string;
  name: string;
  email: string;
  type: 'Host' | 'Driver' | 'Broker' | 'Renter' | 'Liaison';
  riskScore: number;
  status: 'Active' | 'Flagged' | 'Suspended';
  location: string;
  bookings: number;
  lastActivity: string;
  avatar?: string;
  profileDetails?: {
    reviews: number;
    averageRating: number;
    cancellations: number;
    cancellationRate: number;
    swaps: number;
    swapRate: number;
    documents: number;
    businessReg: boolean;
    taxId: boolean;
    flags: string[];
  };
}

interface RiskFactor {
  id: string;
  category: 'Cancellations' | 'Swaps/Substitutions' | 'Fraud Alerts' | 'Review Analysis';
  impact: 'High Impact' | 'Medium Impact' | 'Critical Impact';
  description: string;
  lastFlagged: string;
  details: string;
}

export const UserTrustSafetyPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [userTypeFilter, setUserTypeFilter] = useState('All Users');
  const [riskScoreFilter, setRiskScoreFilter] = useState('All Scores');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendDuration, setSuspendDuration] = useState('3 days');
  const [suspendReason, setSuspendReason] = useState('');
  const [messageTemplate, setMessageTemplate] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [riskLevel, setRiskLevel] = useState('Medium');
  const [riskNote, setRiskNote] = useState('');

  // Mock users data
  const users: User[] = [
    {
      id: '1',
      name: 'John Adebayo',
      email: 'john@example.com',
      type: 'Host',
      riskScore: 85,
      status: 'Active',
      location: 'Lagos',
      bookings: 42,
      lastActivity: '2 hours ago',
      profileDetails: {
        reviews: 18,
        averageRating: 3.6,
        cancellations: 4,
        cancellationRate: 17.4,
        swaps: 7,
        swapRate: 30.4,
        documents: 0,
        businessReg: false,
        taxId: true,
        flags: ['High Swap Rate', 'Document Issue']
      }
    },
    {
      id: '2',
      name: 'Sarah Kimani',
      email: 'sarah@example.com',
      type: 'Driver',
      riskScore: 92,
      status: 'Active',
      location: 'Nairobi',
      bookings: 78,
      lastActivity: '5 mins ago',
      profileDetails: {
        reviews: 45,
        averageRating: 4.8,
        cancellations: 2,
        cancellationRate: 2.6,
        swaps: 1,
        swapRate: 1.3,
        documents: 2,
        businessReg: true,
        taxId: true,
        flags: []
      }
    },
    {
      id: '3',
      name: 'Michael Osei',
      email: 'michael@example.com',
      type: 'Broker',
      riskScore: 45,
      status: 'Flagged',
      location: 'Accra',
      bookings: 23,
      lastActivity: '1 day ago',
      profileDetails: {
        reviews: 8,
        averageRating: 2.1,
        cancellations: 12,
        cancellationRate: 52.2,
        swaps: 15,
        swapRate: 65.2,
        documents: 1,
        businessReg: false,
        taxId: false,
        flags: ['High Swap Rate', 'Document Issue', 'Pattern Recognition']
      }
    },
    {
      id: '4',
      name: 'Amara Diallo',
      email: 'amara@example.com',
      type: 'Renter',
      riskScore: 88,
      status: 'Active',
      location: 'Lagos',
      bookings: 15,
      lastActivity: '3 hours ago',
      profileDetails: {
        reviews: 12,
        averageRating: 4.2,
        cancellations: 1,
        cancellationRate: 6.7,
        swaps: 0,
        swapRate: 0,
        documents: 2,
        businessReg: false,
        taxId: true,
        flags: []
      }
    },
    {
      id: '5',
      name: 'James Nkosi',
      email: 'james@example.com',
      type: 'Liaison',
      riskScore: 76,
      status: 'Active',
      location: 'Johannesburg',
      bookings: 36,
      lastActivity: '4 hours ago',
      profileDetails: {
        reviews: 22,
        averageRating: 3.9,
        cancellations: 3,
        cancellationRate: 8.3,
        swaps: 5,
        swapRate: 13.9,
        documents: 1,
        businessReg: true,
        taxId: true,
        flags: ['Document Issue']
      }
    },
    {
      id: '6',
      name: 'Fatima Ahmed',
      email: 'fatima@example.com',
      type: 'Host',
      riskScore: 22,
      status: 'Suspended',
      location: 'Nairobi',
      bookings: 8,
      lastActivity: '5 days ago',
      profileDetails: {
        reviews: 3,
        averageRating: 1.2,
        cancellations: 6,
        cancellationRate: 75,
        swaps: 4,
        swapRate: 50,
        documents: 0,
        businessReg: false,
        taxId: false,
        flags: ['High Swap Rate', 'Document Issue', 'Critical Pattern']
      }
    }
  ];

  const riskFactors: RiskFactor[] = [
    {
      id: '1',
      category: 'Cancellations',
      impact: 'High Impact',
      description: 'Users with 3+ cancellations in 30 days are flagged for review',
      lastFlagged: '2 hours ago',
      details: 'Detected pattern of last-minute cancellations affecting platform reliability'
    },
    {
      id: '2',
      category: 'Swaps/Substitutions',
      impact: 'Medium Impact',
      description: 'Hosts with frequent vehicle swaps reduce service score and penalties',
      lastFlagged: 'Yesterday',
      details: 'High frequency substitutions may indicate fleet management issues'
    },
    {
      id: '3',
      category: 'Fraud Alerts',
      impact: 'Critical Impact',
      description: 'Document inconsistencies, multiple accounts, payment issues',
      lastFlagged: '4 days ago',
      details: 'Automated detection of suspicious account behavior patterns'
    },
    {
      id: '4',
      category: 'Review Analysis',
      impact: 'Medium Impact',
      description: 'Pattern recognition in customer feedback and ratings',
      lastFlagged: 'Today',
      details: 'AI analysis identifies concerning trends in user reviews'
    }
  ];

  const getUserTypeIcon = (type: string) => {
    switch (type) {
      case 'Host': return <Person />;
      case 'Driver': return <DirectionsCar />;
      case 'Broker': return <Business />;
      case 'Renter': return <Groups />;
      case 'Liaison': return <AccountBalance />;
      default: return <Person />;
    }
  };

  const getUserTypeColor = (type: string) => {
    switch (type) {
      case 'Host': return 'primary';
      case 'Driver': return 'success';
      case 'Broker': return 'secondary';
      case 'Renter': return 'info';
      case 'Liaison': return 'warning';
      default: return 'default';
    }
  };

  const getRiskScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Flagged': return 'warning';
      case 'Suspended': return 'error';
      default: return 'default';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'Critical Impact': return 'error';
      case 'High Impact': return 'warning';
      case 'Medium Impact': return 'info';
      default: return 'default';
    }
  };

  const handleViewProfile = (user: User) => {
    setSelectedUser(user);
    setProfileDialogOpen(true);
  };

  const handleSendMessage = (user: User) => {
    setSelectedUser(user);
    setMessageDialogOpen(true);
  };

  const handleSuspendUser = (user: User) => {
    setSelectedUser(user);
    setSuspendDialogOpen(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUserType = userTypeFilter === 'All Users' || user.type === userTypeFilter;
    const matchesRiskScore = riskScoreFilter === 'All Scores' || 
                            (riskScoreFilter === 'High Risk' && user.riskScore >= 80) ||
                            (riskScoreFilter === 'Medium Risk' && user.riskScore >= 60 && user.riskScore < 80) ||
                            (riskScoreFilter === 'Low Risk' && user.riskScore < 60);
    const matchesStatus = statusFilter === 'All Statuses' || user.status === statusFilter;
    const matchesLocation = locationFilter === 'All Locations' || user.location === locationFilter;
    
    return matchesSearch && matchesUserType && matchesRiskScore && matchesStatus && matchesLocation;
  });

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            User Trust & Directory
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all user types with risk scoring and behavioral insights
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Filter Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel>User Type</InputLabel>
            <Select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              label="User Type"
            >
              <MenuItem value="All Users">All Users</MenuItem>
              <MenuItem value="Host">Host</MenuItem>
              <MenuItem value="Driver">Driver</MenuItem>
              <MenuItem value="Broker">Broker</MenuItem>
              <MenuItem value="Renter">Renter</MenuItem>
              <MenuItem value="Liaison">Liaison</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel>Risk Score</InputLabel>
            <Select
              value={riskScoreFilter}
              onChange={(e) => setRiskScoreFilter(e.target.value)}
              label="Risk Score"
            >
              <MenuItem value="All Scores">All Scores</MenuItem>
              <MenuItem value="High Risk">High Risk (80+)</MenuItem>
              <MenuItem value="Medium Risk">Medium Risk (60-79)</MenuItem>
              <MenuItem value="Low Risk">Low Risk (&lt;60)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="All Statuses">All Statuses</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Flagged">Flagged</MenuItem>
              <MenuItem value="Suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <FormControl fullWidth size="small">
            <InputLabel>Location</InputLabel>
            <Select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              label="Location"
            >
              <MenuItem value="All Locations">All Locations</MenuItem>
              <MenuItem value="Lagos">Lagos</MenuItem>
              <MenuItem value="Nairobi">Nairobi</MenuItem>
              <MenuItem value="Accra">Accra</MenuItem>
              <MenuItem value="Johannesburg">Johannesburg</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <IconButton sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <FilterList />
          </IconButton>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Main Directory */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Directory
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    )
                  }}
                  sx={{ minWidth: 250 }}
                />
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Risk Score</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Bookings</TableCell>
                      <TableCell>Last Activity</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ width: 40, height: 40 }}>
                                {user.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {user.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {user.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getUserTypeIcon(user.type)}
                              label={user.type}
                              size="small"
                              color={getUserTypeColor(user.type) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
                              <LinearProgress
                                variant="determinate"
                                value={user.riskScore}
                                color={getRiskScoreColor(user.riskScore) as any}
                                sx={{ width: 60, height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {user.riskScore}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.status}
                              size="small"
                              color={getStatusColor(user.status) as any}
                              variant={user.status === 'Active' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.location}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.bookings}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {user.lastActivity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Profile">
                                <IconButton size="small" onClick={() => handleViewProfile(user)}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send Message">
                                <IconButton size="small" onClick={() => handleSendMessage(user)}>
                                  <Message fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="More Actions">
                                <IconButton size="small">
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[6, 12, 24]}
                component="div"
                count={filteredUsers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Showing 1-6 of 243 users
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            {/* Risk Score Engine */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Risk Score Engine
                  </Typography>

                  <List sx={{ p: 0 }}>
                    {riskFactors.map((factor, index) => (
                      <React.Fragment key={factor.id}>
                        <ListItem sx={{ px: 0, py: 2 }}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {factor.category}
                              </Typography>
                              <Chip
                                label={factor.impact.replace(' Impact', '')}
                                size="small"
                                color={getImpactColor(factor.impact) as any}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {factor.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last flagged: {factor.lastFlagged}
                            </Typography>
                          </Box>
                        </ListItem>
                        {index < riskFactors.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Profile Preview Dialog */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 50, height: 50 }}>
              {selectedUser?.name.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedUser?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.type} • {selectedUser?.location}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="caption">Risk Score:</Typography>
                <Chip
                  label={selectedUser?.riskScore}
                  size="small"
                  color={getRiskScoreColor(selectedUser?.riskScore || 0) as any}
                />
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Profile Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Profile Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Bookings</Typography>
                  <Typography variant="h6">{selectedUser?.bookings} Total</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Reviews</Typography>
                  <Typography variant="h6">{selectedUser?.profileDetails?.reviews} ({selectedUser?.profileDetails?.averageRating}★)</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Cancellations</Typography>
                  <Typography variant="h6">{selectedUser?.profileDetails?.cancellations} ({selectedUser?.profileDetails?.cancellationRate}%)</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Swaps</Typography>
                  <Typography variant="h6">{selectedUser?.profileDetails?.swaps} ({selectedUser?.profileDetails?.swapRate}%)</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Documents</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip
                      icon={selectedUser?.profileDetails?.businessReg ? <CheckCircle /> : <Cancel />}
                      label="Business Reg"
                      size="small"
                      color={selectedUser?.profileDetails?.businessReg ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Chip
                      icon={selectedUser?.profileDetails?.taxId ? <CheckCircle /> : <Cancel />}
                      label="Tax ID"
                      size="small"
                      color={selectedUser?.profileDetails?.taxId ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                {selectedUser?.profileDetails?.flags && selectedUser.profileDetails.flags.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Flags</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {selectedUser.profileDetails.flags.map((flag, index) => (
                        <Chip
                          key={index}
                          label={flag}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 3, textTransform: 'none' }}
              >
                View Full Profile
              </Button>
            </Grid>

            {/* Admin Actions */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Admin Actions
              </Typography>

              {/* Suspend User */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Suspend User
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Temporarily restrict account access
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Select reason...</InputLabel>
                  <Select defaultValue="">
                    <MenuItem value="policy_violation">Policy Violation</MenuItem>
                    <MenuItem value="fraud_concern">Fraud Concern</MenuItem>
                    <MenuItem value="high_risk">High Risk Activity</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <Select value={suspendDuration} onChange={(e) => setSuspendDuration(e.target.value)}>
                    <MenuItem value="3 days">3 days</MenuItem>
                    <MenuItem value="7 days">7 days</MenuItem>
                    <MenuItem value="30 days">30 days</MenuItem>
                    <MenuItem value="permanent">Permanent</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  fullWidth
                  onClick={() => handleSuspendUser(selectedUser!)}
                >
                  Suspend
                </Button>
              </Paper>

              {/* Tag Risk Level */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Tag Risk Level
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Manually adjust risk classification
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {['Low', 'Medium', 'High'].map((level) => (
                    <Button
                      key={level}
                      variant={riskLevel === level ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => setRiskLevel(level)}
                      color={level === 'High' ? 'error' : level === 'Medium' ? 'warning' : 'success'}
                    >
                      {level}
                    </Button>
                  ))}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add note (required)"
                  value={riskNote}
                  onChange={(e) => setRiskNote(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button variant="outlined" size="small" fullWidth>
                  Apply Tag
                </Button>
              </Paper>

              {/* Send Message */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Send Message
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Direct communication with user
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Select template...</InputLabel>
                  <Select
                    value={messageTemplate}
                    onChange={(e) => setMessageTemplate(e.target.value)}
                  >
                    <MenuItem value="policy_reminder">Policy Reminder</MenuItem>
                    <MenuItem value="account_warning">Account Warning</MenuItem>
                    <MenuItem value="verification_request">Verification Request</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Message content..."
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  startIcon={<Send />}
                  onClick={() => handleSendMessage(selectedUser!)}
                >
                  Send Message
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};